import React, { useEffect, useState } from 'react';
import { XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './Button';
import { Input } from './Input';
import { Select } from './Select';
import { Invoice } from './InvoiceCard';
interface EditInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (invoice: Invoice) => void;
  invoice: Invoice | null;
}
export function EditInvoiceModal({
  isOpen,
  onClose,
  onSave,
  invoice
}: EditInvoiceModalProps) {
  const [supplier, setSupplier] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [date, setDate] = useState('');
  const [category, setCategory] = useState<'Sikkerhed' | 'Værktøj' | 'Materialer' | 'Andet'>('Materialer');
  const [type, setType] = useState<'Ladeboks' | 'Andet'>('Ladeboks');
  const [amount, setAmount] = useState('');
  const [paid, setPaid] = useState('no');
  const [notes, setNotes] = useState('');
  useEffect(() => {
    if (invoice) {
      setSupplier(invoice.supplier);
      setInvoiceNumber(invoice.invoiceNumber);
      setDate(invoice.date);
      setCategory(invoice.category);
      setType(invoice.type || 'Ladeboks');
      setAmount(invoice.amount.toString());
      setPaid(invoice.paid ? 'yes' : 'no');
      setNotes(invoice.notes || '');
    }
  }, [invoice]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!invoice || !supplier.trim() || !invoiceNumber.trim() || !date || !amount) return;
    onSave({
      ...invoice,
      supplier,
      invoiceNumber,
      date,
      category,
      type,
      amount: parseFloat(amount),
      paid: paid === 'yes',
      notes: notes || undefined
    });
    onClose();
  };
  if (!invoice) return null;
  return <AnimatePresence data-id="element-317" data-mp="2asisa9wwpub2">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40" onClick={onClose} data-id="element-318" data-mp="2av51edzg4hve" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        duration: 0.3
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto" data-id="element-319" data-mp="blvbzh0l8fda">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 my-8" data-id="element-320" data-mp="3ksuyoeav8aq4">
              <div className="flex items-center justify-between mb-6" data-id="element-321" data-mp="1n0qoawbri4qo">
                <h2 className="text-2xl font-semibold text-stone-900" data-id="element-322" data-mp="1izf9lluff3x9">
                  Rediger faktura
                </h2>
                <button onClick={onClose} className="text-stone-400 hover:text-stone-600 transition-colors" aria-label="Luk" data-id="element-323" data-mp="30wsdg57qborh">
                  <XIcon className="w-6 h-6" data-id="element-324" data-mp="3ann03aow0qdg" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-325" data-mp="3enf0h044nq7q">
                <div data-id="element-326" data-mp="2hyz9r0pa7at1">
                  <h3 className="text-lg font-semibold text-stone-900 mb-4" data-id="element-327" data-mp="2hzhdeie9wraz">
                    Faktura information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-328" data-mp="2mzn0rmdifrn9">
                    <Input label="Leverandør" value={supplier} onChange={e => setSupplier(e.target.value)} placeholder="Indtast leverandør" required data-id="element-329" data-mp="2qxkoslvjo7p2" />

                    <Input label="Faktura nr." value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} placeholder="f.eks. INV-2024-001" required data-id="element-330" data-mp="b6yiw4dxzc9m" />

                    <Input label="Dato" type="date" value={date} onChange={e => setDate(e.target.value)} required data-id="element-331" data-mp="isk4du5iyr3t" />

                    <Select label="Type" value={type} onChange={e => setType(e.target.value as 'Ladeboks' | 'Andet')} options={[{
                  value: 'Ladeboks',
                  label: 'Ladeboks'
                }, {
                  value: 'Andet',
                  label: 'Andet'
                }]} required data-id="element-332" data-mp="snc4p5qhrs2a" />

                    <Select label="Kategori" value={category} onChange={e => setCategory(e.target.value as 'Sikkerhed' | 'Værktøj' | 'Materialer' | 'Andet')} options={[{
                  value: 'Sikkerhed',
                  label: 'Sikkerhed'
                }, {
                  value: 'Værktøj',
                  label: 'Værktøj'
                }, {
                  value: 'Materialer',
                  label: 'Materialer'
                }, {
                  value: 'Andet',
                  label: 'Andet'
                }]} required data-id="element-333" data-mp="1v2hvm8qdf9kn" />

                    <Input label="Beløb (kr)" type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="f.eks. 1500" required data-id="element-334" data-mp="2ld0g2foaq53e" />

                    <Select label="Betalt" value={paid} onChange={e => setPaid(e.target.value)} options={[{
                  value: 'no',
                  label: 'Nej'
                }, {
                  value: 'yes',
                  label: 'Ja'
                }]} data-id="element-335" data-mp="38zglvbp0aw9v" />
                  </div>

                  <div className="mt-4" data-id="element-336" data-mp="928p3vqikpg1">
                    <label className="block text-sm font-medium text-stone-700 mb-1.5" data-id="element-337" data-mp="939zrz3mb9x8">
                      Noter
                    </label>
                    <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Tilføj noter om denne faktura" rows={3} className="w-full px-4 py-2.5 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none" data-id="element-338" data-mp="hv2a89sn6j1t" />
                  </div>
                </div>

                <div className="flex gap-3 pt-2" data-id="element-339" data-mp="2ykr8avirxhka">
                  <Button type="button" variant="secondary" onClick={onClose} className="flex-1" data-id="element-340" data-mp="2ym2cgwg4srbx">
                    Annuller
                  </Button>
                  <Button type="submit" variant="primary" className="flex-1" data-id="element-341" data-mp="2eowjl3rgdoao">
                    Gem ændringer
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}